import type { Ticket } from '@/types/api';
import { UserRole, TicketStatus, TicketPriority } from '@/types/api';

/**
 * Utility functions for ticket sharing functionality
 */

export interface ShareOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: () => Promise<void> | void;
  description?: string;
  requiresPermission?: boolean;
}

export interface ShareUrls {
  internal: string;
  email: string;
}

/**
 * Generate ticket URLs for sharing
 */
export const generateTicketUrls = (ticket: Ticket): ShareUrls => {
  const baseUrl = window.location.origin;
  const ticketId = ticket.ticketNumber || ticket.id;
  
  // Internal app URL (always available)
  const internal = `${baseUrl}/tickets/${ticketId}`;
  
  // Email subject and body
  const emailSubject = encodeURIComponent(`Ticket #${ticketId}: ${ticket.subject}`);
  const emailBody = encodeURIComponent(
    `Hi,\n\nI wanted to share this support ticket with you:\n\n` +
    `Ticket #${ticketId}: ${ticket.subject}\n` +
    `Status: ${getStatusLabel(ticket.status)}\n` +
    `Priority: ${getPriorityLabel(ticket.priority)}\n` +
    `Created: ${new Date(ticket.createdAt).toLocaleDateString('en-AU')}\n\n` +
    `Link: ${internal}\n\n` +
    `Best regards`
  );
  const email = `mailto:?subject=${emailSubject}&body=${emailBody}`;
  
  // No public/external URL is produced. Public ticket sharing is not implemented:
  // there is no server-side token endpoint and no /public/tickets route, so any
  // such URL would be an unauthorised, non-persisted link that 404s (BugHerd #128).
  return {
    internal,
    email
  };
};

/**
 * Copy text to clipboard with enhanced error handling
 */
export const copyToClipboard = async (text: string): Promise<{ success: boolean; message: string }> => {
  try {
    // Modern clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return { success: true, message: 'Copied to clipboard!' };
    }
    
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      return { success: true, message: 'Copied to clipboard!' };
    } else {
      return { success: false, message: 'Failed to copy to clipboard' };
    }
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return { success: false, message: 'Copy not supported in this browser' };
  }
};

/**
 * Check if user can share tickets externally
 */
export const canShareExternally = (userRole: UserRole): boolean => {
  // All authenticated users can share tickets
  return true;
};

/**
 * Get user-friendly status label
 */
export const getStatusLabel = (status: TicketStatus): string => {
  const statusLabels: Record<TicketStatus, string> = {
    [TicketStatus.Open]: 'Open',
    [TicketStatus.InProgress]: 'In Progress',
    [TicketStatus.OnHold]: 'On Hold',
    [TicketStatus.Resolved]: 'Resolved',
    [TicketStatus.Closed]: 'Closed'
  };
  return statusLabels[status] || 'Unknown';
};

/**
 * Get user-friendly priority label
 */
export const getPriorityLabel = (priority: TicketPriority): string => {
  const priorityLabels: Record<TicketPriority, string> = {
    [TicketPriority.Low]: 'Low',
    [TicketPriority.Medium]: 'Medium',
    [TicketPriority.High]: 'High',
    [TicketPriority.Urgent]: 'Urgent'
  };
  return priorityLabels[priority] || 'Unknown';
};

/**
 * Generate share analytics event
 */
export const trackShareEvent = (method: string, ticketId: string, userRole: UserRole): void => {
  // Track sharing analytics
  try {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'share_ticket', {
        method,
        ticket_id: ticketId,
        user_role: userRole
      });
    }
  } catch (err) {
    console.warn('Failed to track share event:', err);
  }
};

/**
 * Validate URL before sharing
 */
export const validateShareUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};